import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import StateManager from 'react-select';
import { ProjectId } from 'api/models';
import { FieldOptionBase } from 'api/tasks';
import { usePopoverActionRef } from 'components/molecules/action-panel';
import {
  AssigneeField,
  AssigneeValue,
} from 'components/organisms/tasks/fields';
// eslint-disable-next-line import/no-internal-modules
import { useSelectedProjectsOptions } from 'hooks/use-selected-projects-options';
import { useTranslation } from 'hooks/use-translation';
import { byLabel } from 'utils/comparators/by-label';
import { queueMacrotask } from 'utils/macrotask';

export type OnAssigneeChange = (value: AssigneeValue[]) => void;

type Props = {
  saveTitle: ReactNode;
  assignee: AssigneeField;
  projectIds: ProjectId[];
  isPrivate: boolean;
  onChange: OnAssigneeChange;
};

export const useAssigneeState = ({
  saveTitle,
  projectIds,
  isPrivate,
  onChange,
}: Props) => {
  const innerRef = usePopoverActionRef();
  const projectIdList = useMemo(
    () => projectIds.map((p) => p[1]),
    [projectIds],
  );

  const options = useSelectedProjectsOptions({
    projectIds: projectIdList,
    isPrivate,
  });

  const [value, setValue] = useState<AssigneeValue[]>([]);
  const flattened = options.flatMap((x) => x.options);
  const assignees = (value ?? [])
    .map(AssigneeValue.toString)
    .map((x) => flattened.find((o) => o.value === x)!)
    .filter(Boolean)
    .sort(byLabel);
  const ref = useRef<StateManager | null>(null);
  const save = useCallback(() => {
    innerRef.current?.hide();
    onChange(value);
  }, [onChange, value, innerRef]);
  const clear = useCallback(() => {
    setValue([]);
    queueMacrotask(() => ref.current?.focus());
  }, [setValue, ref]);
  const primary = useMemo(
    () => ({
      title: saveTitle,
      onClick: save,
      disabled: value.length === 0,
    }),
    [save, value, saveTitle],
  );
  const clearTitle = useTranslation('task_list.bulk_editor.clear');
  const secondary = useMemo(
    () => ({
      title: clearTitle,
      onClick: clear,
      disabled: value.length === 0,
    }),
    [clear, value, clearTitle],
  );
  const cancelTitle = useTranslation('task_list.bulk_editor.cancel');
  const other = useMemo(
    () => [
      {
        title: cancelTitle,
        onClick: () => innerRef.current?.hide(),
      },
    ],
    [innerRef, cancelTitle],
  );
  const onValueChange = useCallback(
    (latest: FieldOptionBase[]) =>
      setValue(
        (latest ?? [])
          .sort(byLabel)
          .map((x) => x.value.toString())
          .map(AssigneeValue.fromString),
      ),
    [setValue],
  );

  return {
    ref,
    clear,
    options,
    assignees,
    onValueChange,
    primary,
    secondary,
    other,
    innerRef,
  };
};
